/**
 * DealEvaluatorAgent — Single-panel Deal Evaluator workspace
 * Authority: DEAL_EVALUATOR_PAGE_SPEC.md + docs/skills/DESIGN_LANGUAGE_SKILL.md
 *            + DS_INPUT_TRIGGER_REDESIGN_2026-06-02.md
 * Design: DS-CARD-V5 tokens from ui-invariants.js — zero inline hex, zero V1 imports.
 *
 * Route: /agents/deal-evaluator
 *
 * Layout:
 *   Single panel — one view at a time.
 *   List view  — full width when no startup is selected.
 *   Detail view — full width when a startup is selected, with back button.
 *
 * # DECISION: Clicking a startup loads it in the detail view without a route change.
 * # DECISION: The /agents/deal-evaluator/:startup_id route still exists for direct links
 *   (handled by DealScreenDetail.jsx page), but the primary UX is single-panel.
 * # DECISION: FCA content is shown as a tab within the detail view (not a separate page tab).
 * # DECISION: The "Back" button inside DealScreenDetailView is hidden in panel mode
 *   by not passing the onBack prop.
 *
 * STAGING MODEL (DS_INPUT_TRIGGER_REDESIGN_2026-06-02):
 *   Uploading/dropping/pasting = STAGING only.
 *   Analysis starts ONLY when CEO clicks the "Analyze Deal" button.
 *   No auto-trigger on file select or drag-drop.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UploadSimple,
  MagnifyingGlass,
  SpinnerGap,
  X as XIcon,
  FilePlus,
  Sparkle,
  LinkSimple,
  TextT,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import useDealScreenStatus from '@/hooks/useDealScreenStatus';
import {
  getDealScreensRecent,
  searchDealScreens,
  runDealScreen,
  rerunDealScreen,
  detectStartupFromDeck,
  runFCAPre,
  runFCAPost,
  recordDealDecision,
} from '@/lib/api';
import StartupDetectionBanner from '@/components/agents/deal-screen/StartupDetectionBanner';
import DealScreenDetailView from '@/components/agents/deal-screen/DealScreenDetailView';
import FCADetailPanel from '@/components/agents/deal-screen/FCADetailPanel';
import useFCAData from '@/hooks/useFCAData';
import {
  DS_TYPOGRAPHY,
  DS_COLORS,
  DS_SPACING,
  DS_BORDERS,
  CALL_BADGE,
} from '@/config/ui-invariants';
import {
  AGENT_CONFIG,
  INTENT_TO_BUTTON,
  FCA_PAGE_CONFIG,
  WORK_TYPES,
  SHARED_UI_LABELS,
} from '@/config/task-display-config';
import { PipelineProgressCard } from '@/components/shared/PipelineProgressCard';

const dealConfig = AGENT_CONFIG.deal_evaluator;
// WI-PROGRESS-CARD-POC: Steps mirror backend deal_screen_pipeline_status_config.json phases array
const PIPELINE_PROGRESS_STEPS = dealConfig.pipeline_phases
  .slice()
  .sort((a, b) => a.order - b.order)
  .map(({ key, label }) => ({ key, label }));
const SEARCH_DEBOUNCE_MS = 300;
const RECENT_STRIP_LIMIT = 4;
// DS_INPUT_TRIGGER_REDESIGN_2026-06-02: max staged inputs before blocking further additions
const STAGING_CAP = 10;

// ── Role inference: given a File object, return 'deck' or 'transcript'
// Authority: DS_INPUT_TRIGGER_REDESIGN_2026-06-02 §2 — role inferred from extension.
function inferRoleFromFile(file) {
  const name = (file?.name || '').toLowerCase();
  if (name.endsWith('.pdf') || name.endsWith('.ppt') || name.endsWith('.pptx')) return 'deck';
  if (name.endsWith('.md') || name.endsWith('.txt') || name.endsWith('.docx')) return 'transcript';
  return null; // unknown — will be rejected at stage time
}

// ── Accepted file extensions (union of deck + transcript allowlists)
const ACCEPTED_FILE_EXTENSIONS = '.pdf,.ppt,.pptx,.md,.txt,.docx';

// ── Role badge: maps kind to display label from config
function RoleBadge({ kind }) {
  const badgeMap = {
    deck:       SHARED_UI_LABELS.stagingRoleBadgeDeck,
    transcript: SHARED_UI_LABELS.stagingRoleBadgeTranscript,
    text:       SHARED_UI_LABELS.stagingRoleBadgeText,
    url:        SHARED_UI_LABELS.stagingRoleBadgeLink,
  };
  const label = badgeMap[kind] || kind;
  return (
    <span style={{
      ...DS_TYPOGRAPHY.badgeText,
      background: DS_COLORS.surface.warm,
      color: DS_COLORS.text.brand,
      border: `1px solid ${DS_COLORS.border.default}`,
      borderRadius: DS_BORDERS.radiusPill,
      padding: '2px 8px',
      whiteSpace: 'nowrap',
      flexShrink: 0,
    }}>
      {label}
    </span>
  );
}

// ── Month/year formatter (never raw ISO) ────────────────────────
function formatMonthYear(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

// ── Compact card for recently analysed strip ────────────────────
function CompactDealCard({ card, onClick }) {
  const callDecision = card.deal_screen?.call_decision;
  const styleEntry = dealConfig.callDecisionStyle?.[callDecision];
  const badgeTokens = styleEntry?.badgeKey === 'worthACall' ? CALL_BADGE.worthACall : CALL_BADGE.notWorthACall;
  const badgeLabel = styleEntry?.label || callDecision || '';
  const ts = card.deal_screen?.updated_at || card.deal_screen?.created_at || card.updated_at;

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        width: '100%',
        padding: '12px 16px',
        background: DS_COLORS.surface.white,
        border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
        borderRadius: DS_BORDERS.radiusCard,
        cursor: 'pointer',
        textAlign: 'left',
        minHeight: `${DS_SPACING.minTouchTarget}px`,
        transition: 'border-color 150ms',
      }}
    >
      {/* Startup name */}
      <span style={{
        ...DS_TYPOGRAPHY.cardTitle,
        color: DS_COLORS.text.primary,
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {card.name || 'Unknown Startup'}
      </span>

      {/* Badge + date cluster */}
      <span style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        {badgeLabel && (
          <span style={{
            background: badgeTokens.background,
            color: badgeTokens.text,
            border: `1px solid ${badgeTokens.border}`,
            borderRadius: CALL_BADGE.badge?.borderRadius || '4px',
            fontSize: CALL_BADGE.badge?.fontSize || '12px',
            fontWeight: CALL_BADGE.badge?.fontWeight || '600',
            padding: '3px 8px',
            whiteSpace: 'nowrap',
          }}>
            {badgeLabel}
          </span>
        )}
        <span style={{
          ...DS_TYPOGRAPHY.bodySm,
          color: DS_COLORS.text.muted,
          whiteSpace: 'nowrap',
        }}>
          {formatMonthYear(ts)}
        </span>
      </span>
    </button>
  );
}

// ── Tab chip for detail view ────────────────────────────────────

function TabChip({ label, active, disabled, onClick }) {
  const shape = CALL_BADGE.badge;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        background: active ? DS_COLORS.text.brand : DS_COLORS.surface.slate,
        color: active ? DS_COLORS.surface.white : DS_COLORS.text.muted,
        borderRadius: shape.borderRadius,
        fontSize: shape.fontSize,
        fontWeight: shape.fontWeight,
        padding: `${shape.paddingTop} ${shape.paddingRight} ${shape.paddingBottom} ${shape.paddingLeft}`,
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: `${DS_SPACING.minTouchTarget}px`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        border: 'none',
      }}
    >
      {label}
    </button>
  );
}

// ── Detail view: tab bar + content ──────────────────────────────

function RightPanel({
  selectedStartupId,
  activeTab,
  setActiveTab,
  canShowFcaTab,
  hasFCA,
  fcaEligible,
  fcaSummary,
  callDecision,
  attachedFiles,
  setAttachedFiles,
  runningFCAPre,
  setRunningFCAPre,
  runningFCAPost,
  setRunningFCAPost,
  fcaRunning,
  setFcaRunning,
  onFcaRunComplete,
}) {
  const aiBtn = INTENT_TO_BUTTON.ai;
  const primaryBtn = INTENT_TO_BUTTON.primary;
  const neutralBtn = INTENT_TO_BUTTON.neutral;
  const hasPreCall = fcaSummary?.pre;
  const hasPostCall = fcaSummary?.post;
  const postCallFileRef = useRef(null);

  // Active FCA trigger chip — defaults to most recent existing FCA
  const [activeChip, setActiveChip] = useState(() => {
    if (fcaSummary?.post) return 'post';
    if (fcaSummary?.pre) return 'pre';
    if (fcaSummary?.kb) return 'kb';
    return 'pre';
  });

  // Reset active chip when summary changes (new startup or FCA completed)
  useEffect(() => {
    if (!fcaSummary) return;
    if (fcaSummary.post) setActiveChip('post');
    else if (fcaSummary.pre) setActiveChip('pre');
    else if (fcaSummary.kb) setActiveChip('kb');
  }, [fcaSummary]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: DS_COLORS.surface.white,
      flex: 1,
    }}>
      {/* Tab bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 24px',
        borderBottom: `1px solid ${DS_COLORS.border.default}`,
        flexShrink: 0,
        background: DS_COLORS.surface.white,
      }}>
        <TabChip
          label={SHARED_UI_LABELS.dealScreenTab}
          active={activeTab === WORK_TYPES.DEAL_SCREEN}
          onClick={() => setActiveTab(WORK_TYPES.DEAL_SCREEN)}
        />
        <TabChip
          label={SHARED_UI_LABELS.firstCutAnalysisTab}
          active={activeTab === 'fca'}
          disabled={!canShowFcaTab}
          onClick={() => setActiveTab('fca')}
        />
      </div>

      {/* Panel content — scrollable */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Deal Screen tab */}
        {activeTab === WORK_TYPES.DEAL_SCREEN && (
          <DealScreenDetailView
            startupId={selectedStartupId}
            fcaSummary={fcaSummary}
            // # DECISION: No onBack prop — this is a panel, not a full page.
            // The user navigates via the back button above.
            onSwitchToFCA={() => {
              setActiveTab('fca');
              setFcaRunning((prev) => ({ ...prev, [selectedStartupId]: 'pre' }));
            }}
            onRerun={() => {
              // WI-DS-PROGRESS-VISIBILITY-BUILD-001: Re-arm the parent hook so
              // the PipelineProgressCard reappears during the detail-view Re-run.
              // Mirrors handleRetry: set activeStartupId + clear terminalState.
              setActiveStartupId(selectedStartupId);
              setTerminalState(null);
              setActivePhase('reading');
              localStorage.setItem('deal_eval_active_run', JSON.stringify({
                startupId: selectedStartupId, phase: 'reading', terminal: null,
              }));
            }}
            onReject={async () => {
              try {
                await recordDealDecision(selectedStartupId, 'passed');
                toast.success('Marked as Passed.');
              } catch (err) {
                toast.error(err?.message || 'Could not record decision.');
              }
            }}
          />
        )}

        {/* FCA tab */}
        {activeTab === 'fca' && (
          <div style={{ padding: '24px' }}>
            {/* FCA action row — conditional on startup call decision state */}
            {(() => {
              // State 1: no pre-call FCA — show "Run First Cut Analysis"
              if (!hasPreCall) {
                return (
                  <div style={{ marginBottom: '16px' }}>
                    <button
                      type="button"
                      disabled={runningFCAPre}
                      onClick={async () => {
                        if (runningFCAPre) return;
                        setRunningFCAPre(true);
                        try {
                          await runFCAPre(selectedStartupId);
                          toast.success('First Cut Analysis started.');
                          // Gap 1 + Gap 2: auto-switch to FCA tab and mark running
                          setActiveTab('fca');
                          setFcaRunning((prev) => ({ ...prev, [selectedStartupId]: 'pre' }));
                        } catch (err) {
                          toast.error(err?.message || 'Could not start First Cut Analysis.');
                        } finally {
                          setRunningFCAPre(false);
                        }
                      }}
                      className={`${aiBtn.bg} ${aiBtn.text} ${aiBtn.border} ${aiBtn.hover} border font-heading font-semibold text-sm transition-colors`}
                      style={{
                        minHeight: `${DS_SPACING.minTouchTarget}px`,
                        padding: '10px 20px',
                        borderRadius: DS_BORDERS.radiusButton,
                        cursor: runningFCAPre ? 'wait' : 'pointer',
                        opacity: runningFCAPre ? 0.6 : 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      {runningFCAPre
                        ? 'Analysing…'
                        : <><Sparkle weight="fill" size={16} style={{ marginRight: '6px' }} />{dealConfig.runFcaLabel}</>
                      }
                    </button>
                  </div>
                );
              }

              // State 2: pre-call exists, no post-call — multi-file upload + run
              if (hasPreCall && !hasPostCall) {
                return (
                  <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* Add File button */}
                    <button
                      type="button"
                      onClick={() => postCallFileRef.current?.click()}
                      className={`${neutralBtn.bg} ${neutralBtn.text} ${neutralBtn.border} ${neutralBtn.hover} border font-heading font-semibold text-sm transition-colors`}
                      style={{
                        minHeight: `${DS_SPACING.minTouchTarget}px`,
                        padding: '10px 20px',
                        borderRadius: DS_BORDERS.radiusButton,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        alignSelf: 'flex-start',
                        gap: '8px',
                      }}
                    >
                      <FilePlus weight="bold" style={{ width: '16px', height: '16px' }} />
                      Add File
                    </button>
                    <input
                      ref={postCallFileRef}
                      type="file"
                      accept=".pdf,.docx,.txt"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        // Prevent duplicate by filename
                        setAttachedFiles((prev) =>
                          prev.some((f) => f.name === file.name) ? prev : [...prev, file]
                        );
                        // Reset so the same file can be re-selected after removal
                        e.target.value = '';
                      }}
                      style={{ display: 'none' }}
                    />

                    {/* Attached file list */}
                    {attachedFiles.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {attachedFiles.map((file, idx) => (
                          <div
                            key={`${file.name}-${idx}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              background: DS_COLORS.surface.slate,
                              border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                              borderRadius: DS_BORDERS.radiusButton,
                              minHeight: `${DS_SPACING.minTouchTarget}px`,
                            }}
                          >
                            <span style={{
                              ...DS_TYPOGRAPHY.bodySm,
                              color: DS_COLORS.text.primary,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                            }}>
                              {file.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => setAttachedFiles((prev) => prev.filter((_, i) => i !== idx))}
                              aria-label={`Remove ${file.name}`}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: DS_COLORS.text.muted,
                                padding: '4px',
                                display: 'flex',
                                minWidth: `${DS_SPACING.minTouchTarget}px`,
                                minHeight: `${DS_SPACING.minTouchTarget}px`,
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <XIcon size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Run Post-Call Analysis button */}
                    <button
                      type="button"
                      disabled={runningFCAPost || attachedFiles.length === 0}
                      onClick={async () => {
                        if (runningFCAPost || attachedFiles.length === 0) return;
                        setRunningFCAPost(true);
                        try {
                          await runFCAPost(selectedStartupId, attachedFiles);
                          toast.success('Post-call analysis started.');
                          // # DECISION: Only clear file list on success so CEO doesn't lose uploads on error.
                          setAttachedFiles([]);
                          // Gap 1 + Gap 2: switch to post chip and mark running
                          setActiveChip('post');
                          setFcaRunning((prev) => ({ ...prev, [selectedStartupId]: 'post' }));
                        } catch (err) {
                          toast.error(err?.message || 'Could not start post-call analysis.');
                          // # DECISION: attachedFiles preserved on error so CEO doesn't lose uploads.
                        } finally {
                          setRunningFCAPost(false);
                        }
                      }}
                      className={`${primaryBtn.bg} ${primaryBtn.text} ${primaryBtn.border} ${primaryBtn.hover} border font-heading font-semibold text-sm transition-colors`}
                      style={{
                        minHeight: `${DS_SPACING.minTouchTarget}px`,
                        padding: '10px 20px',
                        borderRadius: DS_BORDERS.radiusButton,
                        cursor: runningFCAPost ? 'wait' : attachedFiles.length === 0 ? 'not-allowed' : 'pointer',
                        opacity: runningFCAPost || attachedFiles.length === 0 ? 0.6 : 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        alignSelf: 'flex-start',
                      }}
                    >
                      {runningFCAPost ? 'Analysing…' : dealConfig.runPostCallLabel}
                    </button>
                  </div>
                );
              }

              // State 3: both exist — no action row needed
              return null;
            })()}

            {/* FCA trigger chips — Pre-Call / Post-Call / Market Intel */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              {['pre', 'post', 'kb'].map((key) => (
                <TabChip
                  key={key}
                  label={FCA_PAGE_CONFIG.triggerLabels[key]?.shortLabel || key}
                  active={activeChip === key}
                  disabled={!fcaSummary?.[key]}
                  onClick={() => setActiveChip(key)}
                />
              ))}
            </div>

            {/* FCA detail panel */}
            <FCADetailPanel
              startupId={selectedStartupId}
              trigger={activeChip}
              isRunning={fcaRunning[selectedStartupId] === activeChip}
              onRunComplete={() => {
                setFcaRunning((prev) => {
                  const next = { ...prev };
                  delete next[selectedStartupId];
                  return next;
                });
                onFcaRunComplete?.();
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Staging area chip — one per staged input ─────────────────────

function StagedInputChip({ item, onRemove }) {
  const displayName = item.name
    || (item.file ? item.file.name : null)
    || (item.value ? item.value.slice(0, 40) : 'Input');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 12px',
        background: DS_COLORS.surface.white,
        border: `1.5px solid ${DS_COLORS.border.default}`,
        borderRadius: DS_BORDERS.radiusCard,
        minHeight: `${DS_SPACING.minTouchTarget}px`,
      }}
    >
      {/* Role badge */}
      <RoleBadge kind={item.kind} />

      {/* Display name */}
      <span
        style={{
          ...DS_TYPOGRAPHY.bodySm,
          color: DS_COLORS.text.primary,
          fontWeight: 600,
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={displayName}
      >
        {displayName}
      </span>

      {/* Remove button — always visible, ≥44px touch target */}
      <button
        type="button"
        onClick={() => onRemove(item.id)}
        aria-label={SHARED_UI_LABELS.removeInputAriaLabel}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: DS_COLORS.text.muted,
          padding: '4px',
          display: 'flex',
          minWidth: `${DS_SPACING.minTouchTarget}px`,
          minHeight: `${DS_SPACING.minTouchTarget}px`,
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          borderRadius: DS_BORDERS.radiusButton,
          transition: 'color 120ms',
        }}
      >
        <XIcon size={16} weight="bold" />
      </button>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────

export default function DealEvaluatorAgent() {
  const navigate = useNavigate();

  // Selected startup — drives detail view
  const [selectedStartupId, setSelectedStartupId] = useState(null);

  // Detail view active tab: WORK_TYPES.DEAL_SCREEN or 'fca'
  const [activeTab, setActiveTab] = useState(WORK_TYPES.DEAL_SCREEN);

  // FCA multi-file upload state (used by FCA post-call tab, unchanged)
  const [attachedFiles, setAttachedFiles] = useState([]);

  // FCA run loading states
  const [runningFCAPre, setRunningFCAPre] = useState(false);
  const [runningFCAPost, setRunningFCAPost] = useState(false);

  // FCA running state per startup — drives FCADetailPanel progress indicator
  const [fcaRunning, setFcaRunning] = useState({});

  // Startup list data
  const [recentCards, setRecentCards] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const searchTimerRef = useRef(null);

  // ── STAGING STATE (DS_INPUT_TRIGGER_REDESIGN_2026-06-02) ──────
  // stagedInputs: Array<{ id, kind, file?, value?, name? }>
  // Replaces the old per-mode immediate-submit pattern.
  const [stagedInputs, setStagedInputs] = useState(() => {
    // On mount: restore text/link/name entries from sessionStorage (files cannot survive)
    try {
      const raw = sessionStorage.getItem('deal_eval_staged_text');
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return [];
  });

  // Inline text paste expansion flag
  const [showTextInput, setShowTextInput] = useState(false);
  const [pendingText, setPendingText] = useState('');
  const [pendingTextName, setPendingTextName] = useState('');

  // Inline link input expansion flag
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [pendingLink, setPendingLink] = useState('');
  const [pendingLinkName, setPendingLinkName] = useState('');

  // Detection state for the deck being staged
  const [detecting, setDetecting] = useState(false);
  const [detectionResult, setDetectionResult] = useState(null);

  // File input refs for the staging file picker
  const fileInputRef = useRef(null);

  // ── Upload / detection state (legacy, kept for compatibility with startPipeline)
  const [submitting, setSubmitting] = useState(false);

  // Active pipeline tracking (persisted to localStorage)
  const [activeStartupId, setActiveStartupId] = useState(() => {
    try {
      const stored = localStorage.getItem('deal_eval_active_run');
      return stored ? JSON.parse(stored).startupId : null;
    } catch { return null; }
  });
  const [activePhase, setActivePhase] = useState(() => {
    try {
      const stored = localStorage.getItem('deal_eval_active_run');
      return stored ? JSON.parse(stored).phase : null;
    } catch { return null; }
  });
  const [terminalState, setTerminalState] = useState(() => {
    try {
      const stored = localStorage.getItem('deal_eval_active_run');
      return stored ? JSON.parse(stored).terminal : null;
    } catch { return null; }
  });
  // Captured business-facing failure message (the hook nulls pipelineStatus once
  // terminalState flips to 'failed', so the card reads from here). + retry lock.
  const [failedMessage, setFailedMessage] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const { status: pipelineStatus } = useDealScreenStatus(
    activeStartupId && !terminalState ? activeStartupId : null
  );

  // FCA summary for tab enable/disable — probe selected startup
  const { summary: fcaSummary, refreshSummary: refreshFcaSummary } = useFCAData(selectedStartupId, null);
  const hasFCA = fcaSummary && (fcaSummary.pre || fcaSummary.post || fcaSummary.kb);

  // Derive call decision for FCA action row
  const selectedStartupCard = recentCards.find((c) => c.startup_id === selectedStartupId)
    || (searchResults ?? []).find((c) => c.startup_id === selectedStartupId);
  const callDecision = selectedStartupCard?.deal_screen?.call_decision;
  const fcaEligible = !!selectedStartupId;
  const canShowFcaTab = hasFCA || fcaEligible;

  // ── Run-in-progress lock ─────────────────────────────────────
  const runInProgress = !!(
    submitting ||
    (activeStartupId && !terminalState &&
      (!pipelineStatus ||
        pipelineStatus.state === 'running' ||
        pipelineStatus.state === 'not_started'))
  );

  // ── Persist text/link staged items to sessionStorage (files excluded — can't survive reload)
  useEffect(() => {
    const persistable = stagedInputs.filter((item) => !item.file);
    try {
      sessionStorage.setItem('deal_eval_staged_text', JSON.stringify(persistable));
    } catch { /* ignore storage errors */ }
  }, [stagedInputs]);

  // ── beforeunload guard when File objects are staged ───────────
  useEffect(() => {
    const hasFiles = stagedInputs.some((item) => item.file instanceof File);
    if (!hasFiles) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = SHARED_UI_LABELS.stagedFilesBeforeUnloadMsg;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [stagedInputs]);

  // ── Load recent cards ────────────────────────────────────────
  const loadRecent = useCallback(async () => {
    setLoadingRecent(true);
    try {
      const data = await getDealScreensRecent(20);
      setRecentCards(data?.deal_screens ?? []);
    } catch (err) {
      console.error('[DealEvaluator] Failed to load recent:', err);
      toast.error('Could not load recent deal screens.');
    } finally {
      setLoadingRecent(false);
    }
  }, []);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  // ── Pipeline status tracking ─────────────────────────────────
  useEffect(() => {
    if (!pipelineStatus) return;

    const phases = dealConfig.pipeline_phases;
    const currentPhase = pipelineStatus.current_phase;

    if (pipelineStatus.state === 'complete' || pipelineStatus.state === 'partial') {
      setTerminalState('complete');
      setActivePhase(null);
      localStorage.setItem('deal_eval_active_run', JSON.stringify({
        startupId: activeStartupId, phase: null, terminal: 'complete',
      }));
      loadRecent();
      // Auto-select the newly completed startup in the detail view
      if (activeStartupId && !selectedStartupId) {
        setSelectedStartupId(activeStartupId);
      }
    } else if (pipelineStatus.state === 'failed') {
      setTerminalState('failed');
      setActivePhase(null);
      // Capture the business-facing message now — once terminalState flips,
      // the hook stops and pipelineStatus is nulled, so the failed card reads
      // this. Prefer the backend's specific copy; fall back to a generic line.
      setFailedMessage(pipelineStatus.error_message || SHARED_UI_LABELS.dealScreenFailedFallback);
      localStorage.setItem('deal_eval_active_run', JSON.stringify({
        startupId: activeStartupId, phase: null, terminal: 'failed',
      }));
    } else if (currentPhase) {
      const matchedPhase = phases.find((p) => currentPhase.toLowerCase().includes(p.key));
      if (matchedPhase) {
        setActivePhase(matchedPhase.key);
        localStorage.setItem('deal_eval_active_run', JSON.stringify({
          startupId: activeStartupId, phase: matchedPhase.key, terminal: null,
        }));
      }
    }
  }, [pipelineStatus, activeStartupId, loadRecent, selectedStartupId]);

  // ── Search debounce ──────────────────────────────────────────
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!searchQuery.trim()) {
      setSearchResults(null);
      setSearching(false);
      return;
    }

    setSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const results = await searchDealScreens(searchQuery.trim());
        setSearchResults(results?.deal_screens ?? []);
      } catch {
        toast.error('Search failed.');
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery]);

  // ── STAGING HANDLERS ─────────────────────────────────────────

  // Generate a stable client ID for each staged item
  const nextId = useRef(1);
  function makeId() {
    const id = `staged_${nextId.current}`;
    nextId.current += 1;
    return id;
  }

  // File selected or dropped → stage it (no pipeline run)
  const handleFileStage = async (file) => {
    if (!file) return;
    if (stagedInputs.length >= STAGING_CAP) {
      toast.error(SHARED_UI_LABELS.stagingCapMessage);
      return;
    }

    const role = inferRoleFromFile(file);
    if (!role) {
      toast.error(`Unsupported file type: ${file.name.split('.').pop().toUpperCase()}. Please add a PDF, PPT, PPTX, MD, TXT, or DOCX file.`);
      return;
    }

    // Dedup by filename
    if (stagedInputs.some((item) => item.file?.name === file.name)) {
      toast.error(`${file.name} is already staged.`);
      return;
    }

    const newItem = { id: makeId(), kind: role, file, name: file.name };
    setStagedInputs((prev) => [...prev, newItem]);

    // WI-DEAL-SCREEN-UPLOAD-ROBUSTNESS-001 / Priority 1 & 3:
    // Startup detection runs ONLY for deck files. /detect is deck-only — calling it
    // for a transcript both (a) swallowed a 400 "Unsupported file type" (Priority 3)
    // and (b) cleared the deck's detection match below, so the matched startup_id
    // never reached /run and the backend created a DUPLICATE startup (Priority 1).
    // Gating to deck-role preserves the deck's match across transcript staging.
    if (role !== 'deck') return;

    // Run detection informational only — never triggers analysis
    setDetecting(true);
    setDetectionResult(null);
    try {
      const result = await detectStartupFromDeck(file);
      if (result?.state === 'match_found' || result?.state === 'possible_match') {
        setDetectionResult(result);
      }
    } catch {
      // # DECISION: Detection errors are silent — staging succeeds regardless.
    } finally {
      setDetecting(false);
    }
  };

  // Handle the file picker onChange
  const handleFilePicker = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleFileStage(file);
    e.target.value = ''; // reset so same file can be re-selected after removal
  };

  // Drag-drop handlers — stage the file, never trigger analysis
  const [dragHighlight, setDragHighlight] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragHighlight(false);
    if (runInProgress) return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileStage(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!dragHighlight) setDragHighlight(true);
  };

  const handleDragLeave = () => setDragHighlight(false);

  // Stage a text item
  const handleStageText = () => {
    const trimmed = pendingText.trim();
    if (!trimmed) {
      toast.error('Please paste some text before adding.');
      return;
    }
    if (stagedInputs.length >= STAGING_CAP) {
      toast.error(SHARED_UI_LABELS.stagingCapMessage);
      return;
    }
    const newItem = {
      id: makeId(),
      kind: 'text',
      value: trimmed,
      name: pendingTextName.trim() || trimmed.slice(0, 32),
    };
    setStagedInputs((prev) => [...prev, newItem]);
    setPendingText('');
    setPendingTextName('');
    setShowTextInput(false);
  };

  // Stage a link item
  const handleStageLink = () => {
    const trimmed = pendingLink.trim();
    if (!trimmed) {
      toast.error('Please paste a URL before adding.');
      return;
    }
    if (stagedInputs.length >= STAGING_CAP) {
      toast.error(SHARED_UI_LABELS.stagingCapMessage);
      return;
    }
    const newItem = {
      id: makeId(),
      kind: 'url',
      value: trimmed,
      name: pendingLinkName.trim() || trimmed,
    };
    setStagedInputs((prev) => [...prev, newItem]);
    setPendingLink('');
    setPendingLinkName('');
    setShowLinkInput(false);
  };

  // Remove a staged item by ID
  const handleRemoveStagedItem = (id) => {
    // WI-DEAL-SCREEN-UPLOAD-ROBUSTNESS-001 / Priority 1:
    // Clear the detection banner ONLY when the removed item is the deck that produced
    // it. Removing a transcript/text/url must NOT drop the deck's matched startup_id,
    // or the next /run would create a duplicate startup.
    const removed = stagedInputs.find((item) => item.id === id);
    setStagedInputs((prev) => prev.filter((item) => item.id !== id));
    if (removed?.kind === 'deck') {
      setDetectionResult(null);
    }
  };

  // ── THE SINGLE ANALYZE TRIGGER ───────────────────────────────
  // Called only when CEO clicks "Analyze Deal". Snapshots stagedInputs at click time.
  const handleAnalyze = async () => {
    if (runInProgress || stagedInputs.length === 0) return;

    // Client-side lock: disable button immediately to prevent double-click
    setSubmitting(true);

    const snapshot = [...stagedInputs];
    // Resolve startup_id: prefer detection result startup_id if CEO hasn't dismissed it
    const resolvedStartupId = detectionResult?.match?.startup_id || undefined;

    try {
      const result = await runDealScreen({
        stagedInputs: snapshot,
        startupId: resolvedStartupId,
      });

      const sid = result?.startup_id;
      if (sid) {
        setActiveStartupId(sid);
        setActivePhase('reading');
        setTerminalState(null);
        localStorage.setItem('deal_eval_active_run', JSON.stringify({
          startupId: sid, phase: 'reading', terminal: null,
        }));
        toast.success('Analysis started.');
        // Clear staging area on successful submit
        setStagedInputs([]);
        setDetectionResult(null);
        try { sessionStorage.removeItem('deal_eval_staged_text'); } catch { /* ignore */ }
        // Pre-select the startup in the detail view
        setSelectedStartupId(sid);
        setActiveTab(WORK_TYPES.DEAL_SCREEN);
      } else {
        // WI-DEAL-SCREEN-UPLOAD-ROBUSTNESS-001 / Priority 2 ("never silently do nothing"):
        // A 200 without a startup_id means the run could not be started. Clear the stale
        // active-run lock AND surface a visible error instead of silently doing nothing.
        setActiveStartupId(null);
        setTerminalState(null);
        localStorage.removeItem('deal_eval_active_run');
        toast.error('Analysis could not start. Please try again.');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to start analysis.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Retry a failed run (WI-DS-STUCK-RUN-RCA-RETRY-001) ─────────
  // Reuses the SINGLE Re-run-from-Deck authority (/deal-screen/{id}/rerun) — one
  // run path, never a second. Idempotency-guarded: the `retrying` flag disables
  // the button on tap and the backend's _processing_state guard rejects a
  // concurrent run. Clears terminalState so the status hook re-enters polling
  // (a fresh poll window, so the escape-hatch deadline restarts). If the run has
  // no deck cache to rerun, the backend responds with a truthful "re-upload"
  // failed status — never a silent no-op.
  const handleRetry = async () => {
    if (!activeStartupId || retrying) return;
    setRetrying(true);
    try {
      await rerunDealScreen(activeStartupId);
      setFailedMessage(null);
      setTerminalState(null);
      setActivePhase('reading');
      localStorage.setItem('deal_eval_active_run', JSON.stringify({
        startupId: activeStartupId, phase: 'reading', terminal: null,
      }));
      toast.success('Restarting analysis…');
    } catch (err) {
      toast.error(err?.message || 'Could not restart. Please re-add the deck and try again.');
    } finally {
      setRetrying(false);
    }
  };

  const handleViewPrevious = () => {
    if (!detectionResult?.match?.startup_id) return;
    navigate(`/startups/${detectionResult.match.startup_id}`);
  };

  const handleNotSameStartup = () => {
    // Dismiss the detection result — the staged file stays; analysis will treat as new startup
    setDetectionResult(null);
  };

  const handleCardClick = (startupId) => {
    setSelectedStartupId(startupId);
    setActiveTab(WORK_TYPES.DEAL_SCREEN);
    // Reset FCA file state and loading states when switching startups
    setAttachedFiles([]);
    setRunningFCAPre(false);
    setRunningFCAPost(false);
  };

  // ── Render ───────────────────────────────────────────────────

  // A run reached a TRUTHFUL terminal failure (backend FAILED, or the hook's
  // client-side escape hatch). Show a plain-English error + Retry instead of an
  // endless spinner / silently-locked button. WI-DS-STUCK-RUN-RCA-RETRY-001.
  const failedRunActive = !!(activeStartupId && terminalState === 'failed');

  const renderFailedRunCard = () => (
    <div
      role="alert"
      style={{
        background: DS_COLORS.error.background,
        border: DS_COLORS.error.border,
        borderRadius: DS_BORDERS.radiusButton,
        padding: '20px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <div style={{
        fontFamily: 'DM Sans, system-ui, sans-serif',
        fontWeight: 700,
        fontSize: '20px',
        color: DS_COLORS.text.error,
      }}>
        {SHARED_UI_LABELS.dealScreenFailedHeading}
      </div>
      <div style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '16px',
        fontWeight: 500,
        color: DS_COLORS.text.primary,
        lineHeight: 1.5,
      }}>
        {failedMessage || SHARED_UI_LABELS.dealScreenFailedFallback}
      </div>
      <div style={{ marginTop: '4px' }}>
        <button
          type="button"
          onClick={handleRetry}
          disabled={retrying}
          aria-label={retrying ? SHARED_UI_LABELS.dealScreenRetryingLabel : SHARED_UI_LABELS.dealScreenRetryButton}
          style={{
            minHeight: `${DS_SPACING.minTouchTarget}px`,
            padding: '10px 24px',
            borderRadius: DS_BORDERS.radiusButton,
            background: DS_COLORS.text.brand,
            color: DS_COLORS.surface.white,
            border: 'none',
            fontFamily: 'DM Sans, system-ui, sans-serif',
            fontWeight: 700,
            fontSize: '16px',
            cursor: retrying ? 'not-allowed' : 'pointer',
            opacity: retrying ? 0.6 : 1,
          }}
        >
          {retrying ? SHARED_UI_LABELS.dealScreenRetryingLabel : SHARED_UI_LABELS.dealScreenRetryButton}
        </button>
      </div>
    </div>
  );

  // Recent strip: max RECENT_STRIP_LIMIT cards. Search results: show all.
  const displayCards = searchResults !== null ? searchResults : recentCards.slice(0, RECENT_STRIP_LIMIT);
  const neutralBtn = INTENT_TO_BUTTON.neutral;
  const aiBtn = INTENT_TO_BUTTON.ai;

  // ── Detail view (selectedStartupId is set) ────────────────────
  if (selectedStartupId) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        background: DS_COLORS.surface.warm,
      }}>
        {/* Back button */}
        <div style={{ padding: '16px 24px 0 24px', flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => setSelectedStartupId(null)}
            className={`${neutralBtn.bg} ${neutralBtn.text} ${neutralBtn.border} ${neutralBtn.hover} border font-heading font-semibold text-sm transition-colors`}
            style={{
              minHeight: `${DS_SPACING.minTouchTarget}px`,
              padding: '10px 20px',
              borderRadius: DS_BORDERS.radiusButton,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            {'← All Startups'}
          </button>
        </div>

        {/* Pipeline progress — visible in detail view while analysis runs */}
        {activeStartupId === selectedStartupId && pipelineStatus && !['complete', 'partial', 'failed'].includes(pipelineStatus.state) && (
          <div style={{ padding: '0 24px 8px 24px', flexShrink: 0 }}>
            <PipelineProgressCard
              steps={PIPELINE_PROGRESS_STEPS}
              currentPhase={pipelineStatus.current_phase}
              state={pipelineStatus.state}
              progressDetail={pipelineStatus.progress_detail}
            />
          </div>
        )}
        {/* Failed run — plain-English error + Retry (replaces the spinner) */}
        {activeStartupId === selectedStartupId && failedRunActive && (
          <div style={{ padding: '0 24px 8px 24px', flexShrink: 0 }}>
            {renderFailedRunCard()}
          </div>
        )}

        {/* Detail content — full width */}
        <RightPanel
          selectedStartupId={selectedStartupId}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          canShowFcaTab={canShowFcaTab}
          hasFCA={hasFCA}
          fcaEligible={fcaEligible}
          fcaSummary={fcaSummary}
          callDecision={callDecision}
          attachedFiles={attachedFiles}
          setAttachedFiles={setAttachedFiles}
          runningFCAPre={runningFCAPre}
          setRunningFCAPre={setRunningFCAPre}
          runningFCAPost={runningFCAPost}
          setRunningFCAPost={setRunningFCAPost}
          fcaRunning={fcaRunning}
          setFcaRunning={setFcaRunning}
          onFcaRunComplete={refreshFcaSummary}
        />
      </div>
    );
  }

  // ── List view (no startup selected) ───────────────────────────
  const analyzeDisabled = runInProgress || stagedInputs.length === 0;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
      background: DS_COLORS.surface.warm,
    }}>
      {/* Page header */}
      <div style={{
        padding: '20px 24px 12px 24px',
        borderBottom: `1px solid ${DS_COLORS.border.default}`,
        flexShrink: 0,
        background: DS_COLORS.surface.white,
      }}>
        <h1 style={{
          ...DS_TYPOGRAPHY.displayXL,
          color: DS_COLORS.text.brand,
          margin: 0,
          marginBottom: '4px',
        }}>
          Deal Evaluator
        </h1>
        <p style={{
          ...DS_TYPOGRAPHY.bodySm,
          color: DS_COLORS.text.secondary,
          margin: 0,
        }}>
          Stage inputs then click Analyze Deal to evaluate a startup.
        </p>
      </div>

      {/* Scrollable content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}>
        {/* Pipeline progress — shown in list view while an analysis runs */}
        {activeStartupId && pipelineStatus && !['complete', 'partial', 'failed'].includes(pipelineStatus.state) && (
          <PipelineProgressCard
            steps={PIPELINE_PROGRESS_STEPS}
            currentPhase={pipelineStatus.current_phase}
            state={pipelineStatus.state}
            progressDetail={pipelineStatus.progress_detail}
          />
        )}
        {/* Failed run — plain-English error + Retry (replaces the spinner) */}
        {failedRunActive && (
          <div style={{ marginBottom: '12px' }}>
            {renderFailedRunCard()}
          </div>
        )}

        {/* ── STAGING AREA ── */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          style={{
            border: `2px dashed ${dragHighlight && !runInProgress ? DS_COLORS.text.brand : DS_COLORS.border.default}`,
            borderRadius: DS_BORDERS.radiusCard,
            background: dragHighlight && !runInProgress ? DS_COLORS.surface.warm : DS_COLORS.surface.slate,
            padding: `${DS_SPACING.cardPadding}px`,
            transition: 'all 150ms',
            cursor: runInProgress ? 'not-allowed' : 'default',
          }}
        >
          {/* Startup detection banner — informational only */}
          {detectionResult && (
            <div style={{ marginBottom: '12px' }}>
              <StartupDetectionBanner
                detectionResult={detectionResult}
                onViewPrevious={handleViewPrevious}
                onNotSameStartup={handleNotSameStartup}
                loading={runInProgress}
              />
            </div>
          )}

          {/* Detecting spinner */}
          {detecting && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
              <SpinnerGap size={16} style={{ color: DS_COLORS.text.muted, animation: 'spin 1s linear infinite' }} />
              <span style={{ ...DS_TYPOGRAPHY.bodySm, color: DS_COLORS.text.muted }}>
                {SHARED_UI_LABELS.stagingDetectingLabel}
              </span>
            </div>
          )}

          {/* Staged inputs list */}
          {stagedInputs.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {stagedInputs.map((item) => (
                <StagedInputChip
                  key={item.id}
                  item={item}
                  onRemove={handleRemoveStagedItem}
                />
              ))}
            </div>
          )}

          {/* Cap message */}
          {stagedInputs.length >= STAGING_CAP && (
            <p style={{
              ...DS_TYPOGRAPHY.bodySm,
              color: DS_COLORS.text.error,
              margin: '0 0 12px 0',
            }}>
              {SHARED_UI_LABELS.stagingCapMessage}
            </p>
          )}

          {/* Add affordances row */}
          {stagedInputs.length < STAGING_CAP && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {/* Add deck/transcript file */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={runInProgress}
                className={`${neutralBtn.bg} ${neutralBtn.text} ${neutralBtn.border} ${neutralBtn.hover} border font-heading font-semibold text-sm transition-colors`}
                style={{
                  minHeight: `${DS_SPACING.minTouchTarget}px`,
                  padding: '10px 16px',
                  borderRadius: DS_BORDERS.radiusButton,
                  cursor: runInProgress ? 'not-allowed' : 'pointer',
                  opacity: runInProgress ? 0.5 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <UploadSimple weight="bold" size={16} />
                {SHARED_UI_LABELS.addDeckTranscriptButton}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_EXTENSIONS}
                onChange={handleFilePicker}
                style={{ display: 'none' }}
              />

              {/* Paste text */}
              <button
                type="button"
                onClick={() => { setShowTextInput((v) => !v); setShowLinkInput(false); }}
                disabled={runInProgress}
                className={`${neutralBtn.bg} ${neutralBtn.text} ${neutralBtn.border} ${neutralBtn.hover} border font-heading font-semibold text-sm transition-colors`}
                style={{
                  minHeight: `${DS_SPACING.minTouchTarget}px`,
                  padding: '10px 16px',
                  borderRadius: DS_BORDERS.radiusButton,
                  cursor: runInProgress ? 'not-allowed' : 'pointer',
                  opacity: runInProgress ? 0.5 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <TextT weight="bold" size={16} />
                {SHARED_UI_LABELS.pasteTextButton}
              </button>

              {/* Add link */}
              <button
                type="button"
                onClick={() => { setShowLinkInput((v) => !v); setShowTextInput(false); }}
                disabled={runInProgress}
                className={`${neutralBtn.bg} ${neutralBtn.text} ${neutralBtn.border} ${neutralBtn.hover} border font-heading font-semibold text-sm transition-colors`}
                style={{
                  minHeight: `${DS_SPACING.minTouchTarget}px`,
                  padding: '10px 16px',
                  borderRadius: DS_BORDERS.radiusButton,
                  cursor: runInProgress ? 'not-allowed' : 'pointer',
                  opacity: runInProgress ? 0.5 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <LinkSimple weight="bold" size={16} />
                {SHARED_UI_LABELS.addLinkButton}
              </button>
            </div>
          )}

          {/* Inline text paste form */}
          {showTextInput && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              marginBottom: '12px',
              padding: '12px',
              background: DS_COLORS.surface.white,
              border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
              borderRadius: DS_BORDERS.radiusCard,
            }}>
              <input
                type="text"
                value={pendingTextName}
                onChange={(e) => setPendingTextName(e.target.value)}
                placeholder={SHARED_UI_LABELS.stagingNamePlaceholder}
                style={{
                  width: '100%', padding: '10px 12px',
                  borderRadius: DS_BORDERS.radiusButton,
                  border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                  ...DS_TYPOGRAPHY.bodySm, color: DS_COLORS.text.primary,
                  background: DS_COLORS.surface.white, outline: 'none',
                  minHeight: `${DS_SPACING.minTouchTarget}px`, boxSizing: 'border-box',
                }}
              />
              <textarea
                value={pendingText}
                onChange={(e) => setPendingText(e.target.value)}
                placeholder={SHARED_UI_LABELS.stagingPasteTextPlaceholder}
                rows={5}
                style={{
                  width: '100%', padding: '10px 12px',
                  borderRadius: DS_BORDERS.radiusButton,
                  border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                  ...DS_TYPOGRAPHY.bodySm, color: DS_COLORS.text.primary,
                  background: DS_COLORS.surface.white, outline: 'none',
                  resize: 'vertical', boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={handleStageText}
                  className={`${neutralBtn.bg} ${neutralBtn.text} ${neutralBtn.border} ${neutralBtn.hover} border font-heading font-semibold text-sm transition-colors`}
                  style={{
                    minHeight: `${DS_SPACING.minTouchTarget}px`, padding: '10px 16px',
                    borderRadius: DS_BORDERS.radiusButton, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center',
                  }}
                >
                  Add Text
                </button>
                <button
                  type="button"
                  onClick={() => { setShowTextInput(false); setPendingText(''); setPendingTextName(''); }}
                  style={{
                    minHeight: `${DS_SPACING.minTouchTarget}px`, padding: '10px 16px',
                    borderRadius: DS_BORDERS.radiusButton, cursor: 'pointer',
                    background: 'none', border: 'none',
                    ...DS_TYPOGRAPHY.bodySm, color: DS_COLORS.text.muted,
                    display: 'inline-flex', alignItems: 'center',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Inline link form */}
          {showLinkInput && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              marginBottom: '12px',
              padding: '12px',
              background: DS_COLORS.surface.white,
              border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
              borderRadius: DS_BORDERS.radiusCard,
            }}>
              <input
                type="text"
                value={pendingLinkName}
                onChange={(e) => setPendingLinkName(e.target.value)}
                placeholder={SHARED_UI_LABELS.stagingNamePlaceholder}
                style={{
                  width: '100%', padding: '10px 12px',
                  borderRadius: DS_BORDERS.radiusButton,
                  border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                  ...DS_TYPOGRAPHY.bodySm, color: DS_COLORS.text.primary,
                  background: DS_COLORS.surface.white, outline: 'none',
                  minHeight: `${DS_SPACING.minTouchTarget}px`, boxSizing: 'border-box',
                }}
              />
              <input
                type="url"
                value={pendingLink}
                onChange={(e) => setPendingLink(e.target.value)}
                placeholder={SHARED_UI_LABELS.stagingLinkPlaceholder}
                style={{
                  width: '100%', padding: '10px 12px',
                  borderRadius: DS_BORDERS.radiusButton,
                  border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                  ...DS_TYPOGRAPHY.bodySm, color: DS_COLORS.text.primary,
                  background: DS_COLORS.surface.white, outline: 'none',
                  minHeight: `${DS_SPACING.minTouchTarget}px`, boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={handleStageLink}
                  className={`${neutralBtn.bg} ${neutralBtn.text} ${neutralBtn.border} ${neutralBtn.hover} border font-heading font-semibold text-sm transition-colors`}
                  style={{
                    minHeight: `${DS_SPACING.minTouchTarget}px`, padding: '10px 16px',
                    borderRadius: DS_BORDERS.radiusButton, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center',
                  }}
                >
                  Add Link
                </button>
                <button
                  type="button"
                  onClick={() => { setShowLinkInput(false); setPendingLink(''); setPendingLinkName(''); }}
                  style={{
                    minHeight: `${DS_SPACING.minTouchTarget}px`, padding: '10px 16px',
                    borderRadius: DS_BORDERS.radiusButton, cursor: 'pointer',
                    background: 'none', border: 'none',
                    ...DS_TYPOGRAPHY.bodySm, color: DS_COLORS.text.muted,
                    display: 'inline-flex', alignItems: 'center',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Empty state helper */}
          {stagedInputs.length === 0 && !showTextInput && !showLinkInput && (
            <p style={{
              ...DS_TYPOGRAPHY.bodySm,
              color: DS_COLORS.text.muted,
              margin: 0,
              textAlign: 'center',
            }}>
              {SHARED_UI_LABELS.stagingEmptyHelper}
            </p>
          )}

          {/* THE SINGLE ANALYZE TRIGGER — AI Action class per DESIGN_LANGUAGE_SKILL.md §4 */}
          {/* Violet fill DS_COLORS.ai.action, white text, DM Sans 14px/600, icon+text, ≥44px */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <button
              type="button"
              onClick={handleAnalyze}
              disabled={analyzeDisabled}
              className={`${aiBtn.bg} ${aiBtn.text} ${aiBtn.border} ${aiBtn.hover} border font-heading font-semibold text-sm transition-colors`}
              style={{
                minHeight: `${DS_SPACING.minTouchTarget}px`,
                padding: '10px 24px',
                borderRadius: DS_BORDERS.radiusButton,
                cursor: analyzeDisabled ? 'not-allowed' : 'pointer',
                opacity: analyzeDisabled ? 0.5 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
              aria-label={submitting ? SHARED_UI_LABELS.analyzingLabel : SHARED_UI_LABELS.analyzeDealButton}
            >
              {submitting ? (
                <>
                  <SpinnerGap size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  {SHARED_UI_LABELS.analyzingLabel}
                </>
              ) : (
                <>
                  <Sparkle weight="fill" size={16} />
                  {SHARED_UI_LABELS.analyzeDealButton}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div style={{ position: 'relative' }}>
          <MagnifyingGlass style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: DS_COLORS.text.muted }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={SHARED_UI_LABELS.findStartupByName}
            style={{
              width: '100%',
              paddingLeft: '34px',
              paddingRight: searchQuery ? '32px' : '10px',
              paddingTop: '10px',
              paddingBottom: '10px',
              borderRadius: DS_BORDERS.radiusButton,
              border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
              ...DS_TYPOGRAPHY.bodySm,
              color: DS_COLORS.text.primary,
              background: DS_COLORS.surface.white,
              outline: 'none',
              minHeight: `${DS_SPACING.minTouchTarget}px`,
              boxSizing: 'border-box',
            }}
          />
          {searching && (
            <SpinnerGap style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: DS_COLORS.text.muted, animation: 'spin 1s linear infinite' }} />
          )}
          {searchQuery && !searching && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: DS_COLORS.text.muted, padding: '0', display: 'flex',
              }}
              aria-label={SHARED_UI_LABELS.clearSearch}
            >
              <XIcon size={14} />
            </button>
          )}
        </div>

        {/* Startup list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {loadingRecent ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 0' }}>
              <SpinnerGap style={{ width: '20px', height: '20px', color: DS_COLORS.text.muted, animation: 'spin 1s linear infinite' }} />
              <span style={{ ...DS_TYPOGRAPHY.bodySm, color: DS_COLORS.text.muted, marginLeft: '8px' }}>Loading...</span>
            </div>
          ) : displayCards.length === 0 ? (
            <p style={{ ...DS_TYPOGRAPHY.bodySm, color: DS_COLORS.text.muted, textAlign: 'center', padding: '24px 0', margin: 0 }}>
              {searchResults !== null ? 'No deal screens found for that name.' : 'No deal screens yet.'}
            </p>
          ) : (
            displayCards.map((card) => {
              const sid = card.startup_id;
              return (
                <CompactDealCard
                  key={sid}
                  card={card}
                  onClick={() => handleCardClick(sid)}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
